import { useCallback, useEffect, useRef, useState } from "react"
import { AppData } from "../../App/AppData"
import { useSubscription } from "../../App/useSubscription"
import { IAuditLogEdit, IDay, ILog, Log } from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"

export interface DayEventsResult {
  // The most recently auto-highlighted log id, or `undefined` once cleared/none yet.
  highlight: number | undefined;
  clearHighlight: () => void;
  // Call from the `Subscription.Log` handler for every committed log update -- decides
  // whether it should take the highlight (current day, not a sync replay, not an
  // automatic NMEA entry, and only while the day is focus-editable).
  notifyNewLog: (log: ILog, info: string) => void;
  // Not seeded from an `init*` prop like `logs`/`statuses` (no caller has it available
  // up-front) -- always populated by the acquisition variant's `loadLogs` effect, since
  // `AuditLogEdit` is deliberately `@typegen-skip`'d (issue #529) and so isn't pushed
  // over a live subscription like `logs`/`statuses` are.
  auditLogEdits: IAuditLogEdit[];
  setAuditLogEdits: (edits: IAuditLogEdit[]) => void;
  // Merges a `logs.update` response's `auditLogEdits` into the running list -- see
  // `LogNavigationView`/`LogEnvironmentView`/`LogWatchView`'s `saveEdits` callbacks.
  recordAuditLogEdits: (edits: IAuditLogEdit[]) => void;
  savedDay: IDay | undefined;
  recordSavedDay: (day: IDay) => void;
}

// View-local event handling shared by every DayDataView implementation (legacy and
// the new data-model one): new-log highlighting and audit-log-edit bookkeeping are
// deliberately NOT part of the normalized data model (`AuditLogEdit` is
// `@typegen-skip`'d with no live subscription token) so they can't be migrated onto a
// shared store and must keep being reassembled locally by every acquisition variant.
// The in-progress provisional-log channel used to live here too, but the New variant
// now reads it from the centralized `ProvisionalLog` store (`useProvisionalLogs`); the
// legacy variant keeps the view-local channel via `useViewLocalProvisionalLogs` below,
// which also takes the seed of markers that armed before this view existed (#1218).
export function useDayEvents({ isCurrentDay, focusEditable, initHighlight }: { isCurrentDay: boolean; focusEditable?: boolean; initHighlight?: number }): DayEventsResult {
  const [highlight, setHighlight] = useState<number | undefined>(initHighlight);
  const [auditLogEdits, setAuditLogEdits] = useState<IAuditLogEdit[]>([]);
  const [savedDay, setSavedDay] = useState<IDay | undefined>(undefined);

  const clearHighlight = useCallback(() => {
    setHighlight(undefined);
  }, []);

  const notifyNewLog = useCallback((log: ILog, info: string) => {
    if (!isCurrentDay || info == "sync") {
      return;
    }
    const metadata = Log.parse(log).dataParsed;
    // TODO: Don't highlight automatic entries
    if (!metadata.nmeaAuto && focusEditable) {
      setHighlight(log.id);
    }
  }, [isCurrentDay, focusEditable]);

  const recordAuditLogEdits = useCallback((edits: IAuditLogEdit[]) => {
    setAuditLogEdits((previous) => edits.reduce((acc, edit) => AppData.updateData<IAuditLogEdit>(acc, edit), [...previous]));
  }, []);

  const recordSavedDay = useCallback((day: IDay) => {
    setSavedDay(day);
  }, []);

  return { highlight, clearHighlight, notifyNewLog, auditLogEdits, setAuditLogEdits, recordAuditLogEdits, savedDay, recordSavedDay };
}

// The legacy variant's view-local provisional-log channel: in-progress (uncommitted)
// auto-logs keyed by `metadata.provisionalKey` -- they all share id 0 so they can't
// merge into `logs` by id (see `AppData.mergeProvisional`). Fed by `Log/Provisional`
// and gated to the current day only. The New variant does NOT use this -- it reads the
// same channel from the centralized `ProvisionalLog` store via `useProvisionalLogs`, so
// only one of the two subscriptions is ever live for a given day.
// The legacy (flag-off) view-local provisional channel: today's in-progress auto-logs,
// keyed by `metadata.provisionalKey`. `seeded` carries the markers that were already live
// before this view existed, handed down by `FocusView` (which both subscribes and seeds
// itself from the current-day response). They are broadcast exactly once and are not part
// of the normalized data model, so a day view mounting mid-debounce -- a reload during a
// pending pause, #1218 -- would otherwise render no provisional row until the marker was
// retracted. The New variant gets the same coverage from the store, which `FocusView`'s
// New variant seeds from the same response.
export function useViewLocalProvisionalLogs(isCurrentDay: boolean, seeded?: Record<string, ILog>): Record<string, ILog> {
  const [provisionalLogs, setProvisionalLogs] = useState<Record<string, ILog>>({});
  const seededKeys = useRef<Set<string>>(new Set());

  // Merged rather than assigned, and keyed identically to the live channel, so this is
  // idempotent and a retraction that has already arrived still wins. Each key is taken at
  // most once: a marker the engine has since RETRACTED is gone from this map for good, and
  // re-adding it because it is "missing" again would resurrect a pause that never happened.
  // Tracked in a ref rather than derived from the state, which cannot tell "not seeded yet"
  // from "seeded and then cleared".
  useEffect(() => {
    if (!isCurrentDay || !seeded) {
      return;
    }
    const fresh = Object.entries(seeded).filter(([key]) => !seededKeys.current.has(key));
    if (fresh.length < 1) {
      return;
    }
    for (const [key] of fresh) {
      seededKeys.current.add(key);
    }
    setProvisionalLogs((prev) => ({ ...prev, ...Object.fromEntries(fresh) }));
  }, [isCurrentDay, seeded]);

  useSubscription((log: ILog, _type: string, info: string) => {
    if (!isCurrentDay) {
      return;
    }
    const key = Log.parse(log).dataParsed.provisionalKey;
    setProvisionalLogs((prev) => AppData.updateProvisional(prev, key, log, info));
  }, Subscription.provisionalLog);

  return provisionalLogs;
}
